/**
 * Explicit state machines with out-of-order tolerance.
 *
 * Queue delivery is at-least-once and unordered (ADR 0006), so a handler routinely sees a
 * `processing` event *after* the `succeeded` event it precedes. There are three genuinely
 * different situations and collapsing them is how financial state gets corrupted:
 *
 *   duplicate  — same state arriving again. Do nothing, report success.
 *   stale      — an earlier state arriving late. Ignore it, but record that it happened.
 *   illegal    — a transition that must never occur (a failed payment "succeeding").
 *                Reject loudly; this is a bug or an attack, not a network reordering.
 *
 * The common bug is treating stale as illegal (noisy alerts, failed webhook retries that never
 * drain) or illegal as stale (silent corruption). Both are avoided by ranking the forward
 * lifecycle explicitly.
 */
import { DomainError } from "./errors.js";
export class StateMachine {
    #definition;
    #rank;
    constructor(definition) {
        this.#definition = definition;
        this.#rank = new Map(definition.progression.map((state, index) => [state, index]));
    }
    get name() {
        return this.#definition.name;
    }
    get initial() {
        return this.#definition.initial;
    }
    isTerminal(state) {
        return this.#definition.terminal.includes(state);
    }
    allowedFrom(state) {
        return this.#definition.transitions[state] ?? [];
    }
    /**
     * Classify without throwing. Handlers use this to decide whether to write, skip, or alert.
     */
    classify(from, to) {
        if (from === to)
            return "duplicate";
        if (this.allowedFrom(from).includes(to))
            return "apply";
        const fromRank = this.#rank.get(from);
        const toRank = this.#rank.get(to);
        // Both on the happy path and the incoming state is behind us: a late delivery, not an error.
        if (fromRank !== undefined && toRank !== undefined && toRank < fromRank)
            return "stale";
        return "illegal";
    }
    canTransition(from, to) {
        return this.classify(from, to) === "apply";
    }
    /**
     * Throwing form for command paths, where an illegal transition genuinely is a request error.
     * Webhook handlers should prefer `classify`, since they must return 200 for stale deliveries
     * to stop the provider retrying forever.
     */
    assertTransition(from, to) {
        const outcome = this.classify(from, to);
        if (outcome === "apply" || outcome === "duplicate")
            return;
        throw new DomainError("illegal_state_transition", `${this.#definition.name} cannot move from ${from} to ${to}`, { details: { machine: this.#definition.name, from, to, outcome } });
    }
}
//# sourceMappingURL=state-machine.js.map